import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Container,
    Group,
    Paper,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { requestOtp } from '../api/auth';
import { ColorSchemeToggle } from '../components/ColorSchemeToggle';
import { useAuth } from './auth-context';

export function LoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { loginWithOtp, isAuthenticated, isAdmin } = useAuth();
    const [step, setStep] = useState<'channel' | 'otp'>('channel');
    const [email, setEmail] = useState('');

    const form = useForm({
        initialValues: { email: '', otp: '' },
    });

    const locationError = (location.state as { error?: string } | null)?.error;

    if (isAuthenticated && isAdmin) {
        return <Navigate to="/" replace />;
    }

    const sendOtp = async () => {
        const trimmedEmail = form.values.email.trim();
        if (!trimmedEmail) {
            notifications.show({ color: 'red', message: t('auth.errors.emailRequired') });
            return;
        }
        await requestOtp({ email: trimmedEmail });
        setEmail(trimmedEmail);
        setStep('otp');
        notifications.show({ color: 'green', message: t('auth.notifications.otpSent') });
    };

    const submitOtp = async () => {
        try {
            await loginWithOtp({ email, otp: form.values.otp });
            navigate('/', { replace: true });
        } catch (e) {
            notifications.show({
                color: 'red',
                message: e instanceof Error ? e.message : t('auth.errors.loginFailed'),
            });
        }
    };

    return (
        <Box
            mih="100vh"
            style={{
                display: 'flex',
                alignItems: 'center',
                background:
                    'linear-gradient(135deg, var(--mantine-color-violet-light) 0%, var(--mantine-color-body) 45%)',
            }}
        >
            <Container size="xs" py="xl" w="100%">
                <Group justify="flex-end" mb="md">
                    <ColorSchemeToggle />
                </Group>
                <Paper p="xl" radius="lg" withBorder shadow="md">
                    <Stack>
                        <Stack gap={4}>
                            <Title order={2}>{t('auth.title')}</Title>
                            <Text c="dimmed" size="sm">
                                {t('auth.subtitle')}
                            </Text>
                        </Stack>
                        {locationError ? <Alert color="red">{locationError}</Alert> : null}
                        {step === 'channel' ? (
                            <>
                                <TextInput
                                    label={t('auth.email')}
                                    placeholder={t('common.placeholders.email')}
                                    required
                                    {...form.getInputProps('email')}
                                />
                                <Button onClick={() => void sendOtp()}>{t('auth.sendOtp')}</Button>
                            </>
                        ) : (
                            <>
                                <Text size="sm">{t('auth.codeSentTo', { email })}</Text>
                                <TextInput
                                    label={t('auth.otp')}
                                    placeholder={t('common.placeholders.otp')}
                                    {...form.getInputProps('otp')}
                                />
                                <Button onClick={() => void submitOtp()}>{t('common.actions.verify')}</Button>
                                <Button variant="subtle" onClick={() => setStep('channel')}>
                                    {t('common.actions.back')}
                                </Button>
                            </>
                        )}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}
